import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Award } from 'lucide-react';

export default function AdmissionsPage() {
  const admissionProcess = [
    { step: 1, title: 'Fill Online Form', desc: 'Complete the admission form with all required details' },
    { step: 2, title: 'Document Verification', desc: 'Submit required documents for verification' },
    { step: 3, title: 'Interaction', desc: 'Attend interaction session with student and parent' },
    { step: 4, title: 'Admission Confirmation', desc: 'Receive confirmation and complete fee payment' }
  ];

  const documents = [
    'Birth Certificate',
    'Previous School Transfer Certificate',
    'Previous Year Mark Sheet',
    'Aadhar Card (Student & Parent)',
    '2 Passport Size Photographs',
    'Caste Certificate (if applicable)',
    'Income Certificate (for scholarship)'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Admissions 2026-27</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            Join our family of 1100+ students and experience excellence in education
          </p>
          <Link to="/admission-form">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg px-12 py-6">
              Apply Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Admission Process */}
      <section className="py-20 bg-white" id="process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-blue-900 mb-12 text-center">Admission Process</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {admissionProcess.map((item, i) => (
              <GlassCard key={i} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-20 bg-white" id="documents">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-blue-900 mb-12 text-center">Documents Required</h2>
          <GlassCard>
            <ul className="space-y-4">
              {documents.map((doc, i) => (
                <li key={i} className="flex items-center space-x-3 text-slate-700">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-lg">{doc}</span>
                </li>
              ))}
            </ul>
          </GlassCard>
        </div>
      </section>

      {/* Scholarships */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50" id="scholarships">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 text-amber-600 mx-auto mb-4" />
            <h2 className="text-4xl font-bold text-blue-900 mb-4">Scholarships & Concessions</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We believe every deserving child should have access to quality education
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <GlassCard>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Merit Scholarship</h3>
              <p className="text-slate-600 mb-4">
                Up to 50% fee waiver for students scoring above 90% in board exams
              </p>
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                Up to 50% Off
              </span>
            </GlassCard>

            <GlassCard>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Sports Scholarship</h3>
              <p className="text-slate-600 mb-4">
                Special concessions for students with achievements in sports
              </p>
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                Up to 30% Off
              </span>
            </GlassCard>

            <GlassCard>
              <h3 className="text-xl font-bold text-blue-900 mb-3">Sibling Concession</h3>
              <p className="text-slate-600 mb-4">
                Special discount when two or more siblings are enrolled
              </p>
              <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                15% Off
              </span>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Join Us?</h2>
          <p className="text-blue-100 mb-8 text-lg">
            Start your child's journey towards excellence. Apply now for admission.
          </p>

          <Link to="/admission-form">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg px-12 py-6">
              Fill Admission Form
            </Button>
          </Link>

          <p className="mt-6 text-sm text-blue-200">
            For queries: Call 9243639227 | Email: nsfschool123@gmail.com
          </p>
        </div>
      </section>
    </Layout>
  );
}